import { CellTable } from "@/atoms"
import {
  getDefaultColumnParams,
  getOrderedColumnParams
} from "@/helpers/table.helpers"
import { preventDefaultWrapper } from "@/helpers/utils"
import { HEADING_KEY } from "@/pages/GovernancePage/components/organisms/AiPolicyProvider/getData"
import { StatusAiPolicyCell } from "@/pages/GovernancePage/components/pages/AiPoliciesPage/components/StatusAiPolicyCell"
import { ActionsAiPolicyCell } from "../ActionsAiPolicyCell"
import { EnforcementCell } from "../EnforcementCell"
import { RulesCell } from "../RulesCell"
import { ScopeCell } from "../ScopeCell"
import { ViolatedCell } from "../ViolatedCell"
import { ViolationsAiPolicyCell } from "../ViolationsAiPolicyCell"

import type { ORDER_BY_PROPS } from "@/hooks/useOrderBy"
import type { TFunction } from "@/hooks/useTranslation"
import type { ActionsAiPolicyCellProps } from "@/pages/GovernancePage/components/pages/AiPoliciesPage/components/ActionsAiPolicyCell"
import type { StatusAiPolicyCellProps } from "../StatusAiPolicyCell"

const isHeading = (item: { key: string }) =>
  !!item?.key?.startsWith(HEADING_KEY)

// A heading row is its first cell, spanning the rest
const hiddenOnHeading = (item: { key: string }) =>
  isHeading(item) ? { colSpan: 0 } : {}

export interface LiveColumnsProps extends ActionsAiPolicyCellProps {
  status: StatusAiPolicyCellProps
  // The days a real policy's violations are counted over
  violationsWindowDays: number
  // Opens a violated policy's violation log
  openViolations: (policyId: string) => void
  // The columns sort only with a sorter: scope, name, enforcement,
  // violations and status, the ones the API orders the policies by
  sorter?: Omit<ORDER_BY_PROPS, "onSorterChange">
}

// The demo's columns, as they always were: its violations, and a menu to view
export const getDemoColumnsTable = (
  t: TFunction,
  props: ActionsAiPolicyCellProps
) => {
  return [
    {
      title: "Policy Scope",
      render: ScopeCell,
      onCell: (item: { key: string }) => {
        if (item?.key?.startsWith(HEADING_KEY))
          return {
            colSpan: 6,
            style: {
              fontWeight: 500,
              backgroundColor: "var(--neutral-2-color)"
            }
          }

        return {}
      },
      width: "28%",
      ...getDefaultColumnParams("name")
    },
    {
      title: "Policy Name",
      render: CellTable,
      width: "30%",
      ...getDefaultColumnParams("policyName")
    },
    {
      title: "Rules",
      render: RulesCell,
      width: "14%",
      ...getDefaultColumnParams("description")
    },
    {
      title: "Enforcement",
      render: EnforcementCell(t),
      width: "12%",
      onCell: (item: { key: string }) => {
        if (item?.key?.startsWith(HEADING_KEY)) return { colSpan: 0 }

        return {}
      },
      ...getDefaultColumnParams("enforcement")
    },
    {
      title: "Violations",
      render: ViolatedCell,
      width: "13%",
      ...getDefaultColumnParams("violated")
    },
    {
      width: "3%",
      render: ActionsAiPolicyCell(props),
      onCell: (item: { key: string }) => {
        if (item?.key?.startsWith(HEADING_KEY)) return { colSpan: 0 }

        return {}
      }
    }
  ]
}

// The real policies' columns: their violations, a status switch, and sorting
// by the API
export const getLiveColumnsTable = (t: TFunction, props: LiveColumnsProps) => {
  const { sorter } = props
  const statusColumn = {
    title: t("governance.ai.live.columns.status"),
    render: StatusAiPolicyCell(t, props.status),
    // The SCM policies' status column is 65px; this one also holds a sort
    // arrow, which needs the rest to show "Status" whole
    width: 100,
    ...getOrderedColumnParams("enabled", sorter, "status"),
    // The switch acts in place, without opening the policy
    onCell: (item: { key: string }) =>
      isHeading(item) ? { colSpan: 0 } : { onClick: preventDefaultWrapper() }
  }
  const columns = [
    {
      title: "Policy Scope",
      render: ScopeCell,
      width: "26%",
      ...getOrderedColumnParams("name", sorter, "scope")
    },
    {
      title: "Policy Name",
      render: CellTable,
      // No width: it takes what the others leave, as the SCM policies' name
      // does, so the status column stays as narrow as theirs
      ...getOrderedColumnParams("policyName", sorter, "name"),
      onCell: hiddenOnHeading
    },
    {
      title: "Rules",
      render: RulesCell,
      width: "14%",
      ...getDefaultColumnParams("description"),
      onCell: hiddenOnHeading
    },
    {
      title: "Enforcement",
      render: EnforcementCell(t),
      width: "12%",
      onCell: hiddenOnHeading,
      ...getOrderedColumnParams("enforcement", sorter)
    },
    {
      title: t("governance.ai.live.violations.column"),
      render: ViolationsAiPolicyCell(
        t,
        props.violationsWindowDays,
        props.openViolations
      ),
      width: "12%",
      ...getOrderedColumnParams("violated", sorter),
      onCell: hiddenOnHeading
    },
    statusColumn,
    {
      width: "3%",
      render: ActionsAiPolicyCell(props),
      onCell: hiddenOnHeading
    }
  ]
  const [scopeColumn, ...rest] = columns

  return [
    {
      ...scopeColumn,
      onCell: (item: { key: string }) =>
        isHeading(item)
          ? {
              colSpan: columns.length,
              style: {
                fontWeight: 500,
                backgroundColor: "var(--neutral-2-color)"
              }
            }
          : {}
    },
    ...rest
  ]
}
